import { useDisclosure } from '@mantine/hooks';
import { QUERY } from 'api/Query';
import { UnresolvedCommitDescriptor } from 'custom-types/UnresolvedCommitDescriptor';
import type { JSX } from 'react';
import { useFeatureToggle } from 'ts/base/hooks/UseFeatureToggle';
import { useUiDebuggerEnabled } from 'ts/base/perspective/debugger/UiDebugger.store';
import { TeamscaleLink } from 'ts/base/routing/TeamscaleLink';
import { ClipboardUtils } from 'ts/commons/ClipboardUtils';
import type { CodeLineSelection } from 'ts/commons/code/CodeLineSelection';
import { CodeSelectionUtils } from 'ts/commons/code/CodeSelectionUtils';
import type { PendingFinding } from 'ts/commons/code/RenderedCode.context';
import { useRenderedCodeOptions } from 'ts/commons/code/RenderedCode.context';
import { NavigationHash } from 'ts/commons/NavigationHash';
import { UIUtils } from 'ts/commons/UIUtils';
import { DropdownMenu, DropdownMenuItem } from 'ts/components/DropdownMenu';
import { Icon } from 'ts/components/Icon';
import { ToastNotification } from 'ts/components/Toast';
import { TaskDataContainer } from 'ts/data/TaskDataContainer';
import { REVIEW_FINDING_TYPE_ID } from 'ts/perspectives/metrics/code/FindingsSidebarSection';
import { MarkAsCoveredDialog } from 'ts/perspectives/metrics/code/MarkAsCoveredDialog';
import { ReviewCommentHandler } from 'ts/perspectives/metrics/code/ReviewCommentHandler';
import { openTaskSelectionModal } from 'ts/perspectives/quality_control/tasks/dialog/TaskSelectionDialog';
import { EFeatureToggle } from 'ts/typedefs/EFeatureToggle';
import { useStore } from 'zustand';
import { Links } from '../links/Links';

/**
 * The menu that opens if a user selects a code region. Has to be used inside a {@link RenderedCodeContextProvider} as it
 * uses {@link #useRenderedCodeOptions}
 */
export function CodeSelectionPopup(): JSX.Element | null {
	const { project, uniformPath, commit, reviewFindingsEnabled, highlightedLinesStore } = useRenderedCodeOptions();
	const selectedLines = useStore(highlightedLinesStore, store => store.selectedLines);

	const [showCoverageModal, { open: openCoverageModal, close: closeCoverageModal }] = useDisclosure(false);
	const debuggerEnabled = useUiDebuggerEnabled();
	const isDevMode = useFeatureToggle(EFeatureToggle.ENABLE_DEV_MODE);

	if (!selectedLines) {
		return null;
	}

	return (
		<div onClick={e => e.stopPropagation()}>
			<MarkAsCoveredDialog
				project={project}
				commit={commit}
				selectedLines={selectedLines}
				uniformPath={uniformPath}
				onClose={closeCoverageModal}
				isOpen={showCoverageModal}
				key={selectedLines.firstLine + '-' + selectedLines.lastLine}
			/>

			<DropdownMenu
				trigger={
					<button
						data-testid="code-region-action-menu"
						className="absolute left-0 text-inherit p-0 bg-transparent"
					>
						<Icon name="ellipsis vertical" />
					</button>
				}
			>
				<DropdownMenuItem
					icon="linkify"
					text="Copy link"
					onAction={() => {
						void ClipboardUtils.copyTextToClipboard(CodeSelectionUtils.generateUrl(selectedLines));
					}}
				/>
				<AddToTaskMenuItem selectedLines={selectedLines} />
				<DropdownMenuItem
					icon="times circle"
					text="Deselect"
					onAction={() => {
						highlightedLinesStore.setState({ selectedLines: undefined });
					}}
				/>
				{reviewFindingsEnabled ? <CreateReviewFindingMenuItem selectedLines={selectedLines} /> : null}
				{debuggerEnabled || isDevMode ? (
					<DropdownMenuItem icon="check circle" text="Mark as covered" onAction={openCoverageModal} />
				) : null}
			</DropdownMenu>
		</div>
	);
}

type AddToTaskMenuItemProps = {
	selectedLines: CodeLineSelection;
};

function AddToTaskMenuItem({ selectedLines }: AddToTaskMenuItemProps): JSX.Element {
	const { project, uniformPath, linesWithFindings } = useRenderedCodeOptions();

	const handleClick = () => {
		const findingIdsInSelectedLines = new Set<string>();
		const startLinesOfPendingFindingsInSelection: number[] = [];
		for (let lineNumber = selectedLines.firstLine; lineNumber <= selectedLines.lastLine; lineNumber++) {
			linesWithFindings.get(lineNumber)?.forEach(finding => {
				findingIdsInSelectedLines.add(finding.finding.id);
				if (finding.finding.isPending) {
					startLinesOfPendingFindingsInSelection.push(finding.region.start);
				}
			});
		}
		if (startLinesOfPendingFindingsInSelection.length > 0) {
			UIUtils.showInfoDialog(
				"Can't save pending findings",
				'The review findings starting in line(s) ' +
					startLinesOfPendingFindingsInSelection.join(', ') +
					' might not be stored on the server yet. Please refresh the page and try again.'
			);
			return;
		}
		void getTaskDetailsForCodeSnippet(project, uniformPath, [...findingIdsInSelectedLines], selectedLines).then(
			taskDetails => {
				openTaskSelectionModal(project, taskDetails, taskId =>
					ToastNotification.success(
						<>
							Successfully added code snippet to{' '}
							<TeamscaleLink to={Links.taskDetails(project, taskId)}>Task {taskId}</TeamscaleLink>.
						</>
					)
				);
			}
		);
	};

	return (
		<DropdownMenuItem
			icon={<Icon name="tasks" color="grey" />}
			text="Add code snippet to task"
			onAction={handleClick}
		/>
	);
}

type CreateReviewFindingMenuItemProps = {
	selectedLines: CodeLineSelection;
};

function CreateReviewFindingMenuItem({ selectedLines }: CreateReviewFindingMenuItemProps): JSX.Element {
	const { project, uniformPath, lines, commit, onAddPendingReviewFinding } = useRenderedCodeOptions();

	const handleClick = () => {
		new ReviewCommentHandler(project, uniformPath, lines, commit, selectedLines, (message, startLine, endLine) =>
			onAddPendingReviewFinding?.(createTemporaryReviewFinding(uniformPath, message, startLine, endLine))
		).onAddReviewCommentClicked();
	};

	return <DropdownMenuItem icon="flag" text="Create review finding" onAction={handleClick} />;
}

function createTemporaryReviewFinding(
	uniformPath: string,
	message: string,
	startLine: number,
	endLine: number
): PendingFinding {
	return {
		birth: {
			branchName: '',
			timestamp: Number.MAX_SAFE_INTEGER
		},
		categoryName: '',
		groupName: '',
		properties: {},
		id: 'review-finding-' + Date.now(),
		typeId: REVIEW_FINDING_TYPE_ID,
		analysisTimestamp: 1,
		message,
		location: {
			uniformPath,
			rawStartLine: startLine,
			rawEndLine: endLine,
			rawStartOffset: 0,
			rawEndOffset: 0
		},
		codeScopeName: ''
	};
}

async function getTaskDetailsForCodeSnippet(
	project: string,
	uniformPath: string,
	codeSnippetFindingIds: string[],
	selection: CodeLineSelection
) {
	let commit = NavigationHash.getCurrentCommit();
	const timeCreated = new Date().getTime();
	if (commit == null || commit.isLatestRevision()) {
		commit = UnresolvedCommitDescriptor.createCommitFromTimestamp(timeCreated);
	}
	const resolvedCommit = await QUERY.resolveCommit(project, commit).fetch();
	const textRegionLocation = {
		rawStartOffset: -1,
		rawEndOffset: -1,
		rawStartLine: selection.firstLine,
		rawEndLine: selection.lastLine,
		location: uniformPath,
		uniformPath
	};
	const codeSnippet = {
		location: textRegionLocation,
		commit: resolvedCommit,
		findingIds: codeSnippetFindingIds,
		timeCreated,
		shouldRenderFromFinding: false
	};

	return new TaskDataContainer('Resolve Findings', '', [], [], resolvedCommit.branchName, codeSnippet);
}
